import { useMemo, useState } from "react";
import { useAuth } from "../lib/auth";
import { isPlatformDeveloper } from "../lib/permissions";
import { CHANGELOG, type ChangeType } from "../lib/changelog";

// Repo path used to link raw commit rows to their GitHub page.
// Public repo → no auth required.
const REPO = "bond240/ShiftManager";

// Trailer lines that clutter every commit body without adding
// information for the reader (they're required by tooling but not
// interesting to a supervisor scanning the changelog). Stripped
// before rendering the body.
const TRAILER_PATTERNS = [
  /^Co-Authored-By:.*$/i,
  /^Claude-Session:.*$/i,
];

function cleanBody(body: string): string {
  return body
    .split("\n")
    .filter((line) => !TRAILER_PATTERNS.some((r) => r.test(line.trim())))
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

// Type → colored badge shown on each curated change bullet.
const TYPE_META: Record<ChangeType, { label: string; classes: string }> = {
  new:      { label: "New",      classes: "bg-sky-100 text-sky-800 border-sky-200" },
  improved: { label: "Improved", classes: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  fixed:    { label: "Fixed",    classes: "bg-amber-100 text-amber-800 border-amber-200" },
};

function friendlyDate(ymd: string): string {
  // Accept either "YYYY-MM-DD" or an ISO timestamp; render as
  // "Weekday, Month D, YYYY" in the viewer's locale.
  const d = ymd.length === 10 ? new Date(ymd + "T00:00:00") : new Date(ymd);
  if (Number.isNaN(d.getTime())) return ymd;
  return d.toLocaleDateString(undefined, {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });
}

// Group commits by their calendar date so the raw-log timeline
// reads chronologically instead of as one long list of SHAs.
function groupByDay(entries: typeof __CHANGELOG__): Array<{ day: string; items: typeof __CHANGELOG__ }> {
  const buckets = new Map<string, typeof __CHANGELOG__>();
  const order: string[] = [];
  for (const e of entries) {
    const d = new Date(e.date);
    const day = Number.isNaN(d.getTime())
      ? "Unknown date"
      : d.toLocaleDateString(undefined, {
          weekday: "long", year: "numeric", month: "long", day: "numeric",
        });
    const bucket = buckets.get(day);
    if (bucket) bucket.push(e);
    else { buckets.set(day, [e]); order.push(day); }
  }
  return order.map((day) => ({ day, items: buckets.get(day)! }));
}

export function Changelog() {
  const me = useAuth((s) => s.user);
  const isDev = isPlatformDeveloper(me);
  const [showRaw, setShowRaw] = useState(false);

  const rawEntries = __CHANGELOG__ ?? [];
  const rawGroups = useMemo(() => groupByDay(rawEntries), [rawEntries]);
  const currentSha = __BUILD_SHA__;

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto">
      <header className="mb-4">
        <h1 className="text-xl font-semibold text-slate-900">What's new</h1>
        <p className="text-sm text-slate-500 mt-1">
          Recent updates to ShiftManager. This build is{" "}
          <a
            href={`https://github.com/${REPO}/commit/${currentSha}`}
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono text-slate-700 hover:underline"
          >
            {currentSha}
          </a>
          {__BUILD_CHANNEL__ === "preview" && (
            <span className="ml-2 inline-block rounded bg-amber-100 border border-amber-300 text-amber-800 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider">
              Preview
            </span>
          )}
          .
        </p>
      </header>

      {CHANGELOG.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg p-8 text-center text-sm text-slate-500">
          No release notes yet.
        </div>
      ) : (
        <div className="space-y-6">
          {CHANGELOG.map((release, i) => {
            // Auto-label the "pending" block:
            //   * On preview → "Next release" chip; the bundle
            //     contains these bullets but they're queued and
            //     haven't been promoted to production yet.
            //   * On stable  → THIS IS the current stable build.
            //     The prod bundle was built from source whose top
            //     pending block contained these bullets, so on
            //     stable they ARE in this build. Auto-label with
            //     __BUILD_SHA__ + build date so the header matches
            //     the "This build is X" line at the top.
            const isPending = release.sha === "pending";
            const derivedDate = (() => {
              try { return new Date(__BUILD_AT__).toISOString().slice(0, 10); }
              catch { return undefined; }
            })();
            const displaySha = isPending && __BUILD_CHANNEL__ === "stable"
              ? __BUILD_SHA__
              : release.sha;
            const displayDate = isPending && __BUILD_CHANNEL__ === "stable"
              ? derivedDate
              : release.date;
            return (
            <section key={`${release.sha}-${i}`}>
              <h2 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-baseline gap-2 flex-wrap">
                {isPending && __BUILD_CHANNEL__ === "preview" ? (
                  <>
                    <span className="inline-block rounded bg-amber-100 border border-amber-300 text-amber-800 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider normal-case">
                      Next release
                    </span>
                    <span className="text-slate-500 normal-case font-normal">
                      Currently on preview · not yet promoted to production
                    </span>
                  </>
                ) : (
                  <>
                    <span className="font-mono text-slate-700 normal-case">
                      build {displaySha}
                    </span>
                    {displayDate && (
                      <span className="text-slate-500 normal-case font-normal">
                        · {friendlyDate(displayDate)}
                      </span>
                    )}
                  </>
                )}
              </h2>
              <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
                {release.changes.map((c, i) => {
                  const meta = TYPE_META[c.type];
                  return (
                    <div key={i} className="p-3 flex gap-3">
                      <span
                        className={
                          "shrink-0 mt-0.5 inline-block rounded border w-20 text-center px-1 py-0.5 "
                          + "text-[10px] font-semibold uppercase tracking-wider "
                          + meta.classes
                        }
                      >
                        {meta.label}
                      </span>
                      <div className="min-w-0">
                        <div className="text-sm text-slate-900 font-medium leading-snug">
                          {c.title}
                        </div>
                        {c.detail && (
                          <p className="text-xs text-slate-600 leading-relaxed mt-1">
                            {c.detail}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
            );
          })}
        </div>
      )}

      {/* Technical details toggle — platform developer only. Hidden
        * from agency owners / supervisors so their changelog stays
        * clean of raw commit noise, but I can still get to the SHAs
        * from any browser without switching tools. */}
      {isDev && rawEntries.length > 0 && (
        <div className="mt-8">
          <button
            type="button"
            onClick={() => setShowRaw((v) => !v)}
            className="text-xs text-slate-500 hover:text-slate-800 hover:underline"
          >
            {showRaw ? "▾ Hide technical details" : "▸ Show technical details"}
          </button>
          {showRaw && (
            <div className="mt-3 space-y-6">
              {rawGroups.map((g) => (
                <section key={g.day}>
                  <h2 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
                    {g.day}
                  </h2>
                  <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
                    {g.items.map((e) => {
                      const body = cleanBody(e.body);
                      const time = new Date(e.date).toLocaleTimeString(undefined, {
                        hour: "numeric", minute: "2-digit",
                      });
                      const isCurrent = e.shortSha === currentSha;
                      return (
                        <div key={e.sha} className="p-3">
                          <div className="flex items-baseline justify-between gap-3 mb-1">
                            <div className="flex items-baseline gap-2 min-w-0">
                              <a
                                href={`https://github.com/${REPO}/commit/${e.sha}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-mono text-[11px] text-slate-500 hover:text-slate-800 hover:underline shrink-0"
                              >
                                {e.shortSha}
                              </a>
                              {isCurrent && (
                                <span className="inline-block rounded bg-sky-100 border border-sky-200 text-sky-800 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider shrink-0">
                                  This build
                                </span>
                              )}
                            </div>
                            <span className="text-[11px] text-slate-400 shrink-0">{time}</span>
                          </div>
                          <div className="text-sm text-slate-900 font-medium leading-snug">
                            {e.subject}
                          </div>
                          {body && (
                            <pre className="mt-1.5 whitespace-pre-wrap text-xs text-slate-600 font-sans leading-relaxed">
                              {body}
                            </pre>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
